import type { CaroNotificationType } from '@common/enums';
import type { ObesiQState } from '@common/enums/obesiQState';
import type { ProcessActivityBadge } from '@common/interfaces';
import type { Override } from '@common/utils/utilityTypes';
import type {
    AnderOnderzoek,
    AnderOnderzoekToelichting,
} from '@common/validation/customControls/bariPre/bariPreMedConsultReport/v1/bariPreMedConsultReportSchemaV1';
import type { ConclusieAanvullend, IndicatieVerwijzing } from '@common/validation/customControls/bariPre/utils';
import { QuestionnaireName } from '@common/enums';
import { TotalWeightLossState } from '@common/enums/totalWeightLossState';

export enum DietAndPsychAdvice {
    exclude = 'Patiënt niet includeren, omdat...',
    include = 'Patiënt includeren',
    possible = 'Patiënt mogelijk includeren, mits...',
}

export enum MdoInterimReportAdvice {
    continueWithGuidance = 'Patiënt kan verder, maar heeft extra begeleiding nodig',
    exclude = 'Patiënt niet includeren',
    include = 'Patiënt kan verder',
    notYet = 'Patiënt pauzeren. Na behandeling kan patiënt mogelijk verder',
}

export enum MdoReportAdvice {
    continueWithGuidance = 'Patiënt includeren, maar heeft extra begeleiding nodig',
    exclude = 'Patiënt niet includeren',
    include = 'Patiënt includeren',
    notYet = 'Patiënt pauzeren. Na behandeling kan patiënt mogelijk verder',
}

export enum MedConsultAssessmentAdvice {
    exclude = 'Patiënt niet includeren, omdat...',
    include = 'Patiënt includeren',
    mdo = 'In MDO bespreken',
}

export enum PatientPresence {
    notPresent = 'Patiënt was niet aanwezig',
    present = 'Patiënt was aanwezig',
}

export enum ComplicatieType {
    bloeding = 'Bloeding',
    naadlekkage = 'Naadlekkage',
    overig = 'Overig',
}

export enum Ingreep {
    conversie_maagband_naar_bypass = 'Conversie maagband naar bypass',
    conversie_sleeve_naar_bypass = 'Conversie sleeve naar bypass',
    gastric_bypass = 'Gastric bypass',
    gastric_bypass_met_minimizer_ring = 'Gastric bypass met minimizer ring',
    gastric_sleeve = 'Gastric sleeve',
    gastric_sleeve_met_minimizer_ring = 'Gastric sleeve met minimizer ring',
    omegaloop_bypass = 'Omegaloop bypass',
}

export const bypassSleeveIngreepTypes = {
    [Ingreep.conversie_maagband_naar_bypass]: 'bypass',
    [Ingreep.conversie_sleeve_naar_bypass]: 'bypass',
    [Ingreep.gastric_bypass]: 'bypass',
    [Ingreep.gastric_bypass_met_minimizer_ring]: 'bypass',
    [Ingreep.omegaloop_bypass]: 'bypass',
    [Ingreep.gastric_sleeve]: 'sleeve',
    [Ingreep.gastric_sleeve_met_minimizer_ring]: 'sleeve',
} as const;

export enum Vervolgstap {
    activatielink_opnieuw_versturen = 'Activatielink opnieuw versturen',
    doorverwijzen_extern_buiten_zorgpad = 'Verwijs naar externe behandelaar buiten het zorgpad',
    expert_inschakelen = 'Expert inschakelen',
    geen_actie_nodig = 'Geen verdere actie nodig',
    lifestylecoach_inschakelen = 'Lifestylecoach inschakelen',
    secretariaatsmedewerker_inschakelen = 'Secretariaatsmedewerker inschakelen',
    terug_naar_lifestylecoach = 'Terug naar lifestylecoach',
    vervolgafspraak = 'Vervolgafspraak',
    vervolgafspraak_zelf = 'Maak korte vervolgafspraak bij jezelf',
    verwijs_lifestylecoach_of_ziekenhuis = 'Verwijs terug naar lifestylecoach of verzoek die om patiënt door te sturen naar ziekenhuis',
    vragenlijsten_opnieuw_beschikbaar_maken = 'Vragenlijst(en) opnieuw beschikbaar maken',
    vs_inschakelen = 'Verpleegkundig specialist inschakelen',
}

export enum Expert {
    dietist = 'Diëtist',
    lifestylecoach = 'Lifestylecoach',
    medisch_specialist = 'Medisch specialist',
    psycholoog = 'Psycholoog',
    verpleegkundig_specialist = 'Verpleegkundig specialist',
}

export enum StatusBehandeling {
    afgerond = 'Afgerond',
    nog_bezig = 'Nog bezig',
}

export enum TypeBehandelaar {
    dietist = 'Diëtist',
    internist = 'Internist',
    logopedist = 'Logopedist',
    longarts = 'Longarts',
    mdl_arts = 'MDL-arts',
    overig = 'Overig',
    psychiater = 'Psychiater',
    psycholoog = 'Psycholoog',
}

export const orderedPractitionerTypes: TypeBehandelaar[] = [
    TypeBehandelaar.psycholoog,
    TypeBehandelaar.dietist,
    TypeBehandelaar.longarts,
    TypeBehandelaar.internist,
    TypeBehandelaar.mdl_arts,
    TypeBehandelaar.psychiater,
    TypeBehandelaar.logopedist,
    TypeBehandelaar.overig,
];

export const obesiQQuestionnaireNames = [
    QuestionnaireName.obesiQBodyImage,
    QuestionnaireName.obesiQEatingBehavior,
    QuestionnaireName.obesiQPhysicalFunction,
    QuestionnaireName.obesiQPsychologicalFunction,
    QuestionnaireName.obesiQSexualFunction,
    QuestionnaireName.obesiQSocialFunction,
] as const;

export type ObesiQQuestionnaireName = (typeof obesiQQuestionnaireNames)[number];

export const obesiQThresholds = {
    [QuestionnaireName.obesiQBodyImage]: 53.45,
    [QuestionnaireName.obesiQEatingBehavior]: 56.79,
    [QuestionnaireName.obesiQPhysicalFunction]: 81.23,
    [QuestionnaireName.obesiQPsychologicalFunction]: 61.51,
    [QuestionnaireName.obesiQSexualFunction]: 67.88,
    [QuestionnaireName.obesiQSocialFunction]: 59.53,
};

export const totalWeightLossStatesWithAlarm = [
    TotalWeightLossState.over75,
    TotalWeightLossState.under25,
    TotalWeightLossState.overMedianPlus1SD,
    TotalWeightLossState.overMedianPlus2SD,
    TotalWeightLossState.underMedianMinus1SD,
    TotalWeightLossState.underMedianMinus2SD,
];

export enum FollowUpMoment {
    fiveYears = '5 jaar',
    fourYears = '4 jaar',
    oneYear = '1 jaar',
    sixMonths = '6 maanden',
    threeYears = '3 jaar',
    twoYears = '2 jaar',
}

export const followUpMoments = [
    FollowUpMoment.sixMonths,
    FollowUpMoment.oneYear,
    FollowUpMoment.twoYears,
    FollowUpMoment.threeYears,
    FollowUpMoment.fourYears,
    FollowUpMoment.fiveYears,
] as const;

export const ontlastingType = ['Normaal', 'Obstipatie', 'Diarree'] as const;
export const rokenStatus = ['Ja', 'Nee, nooit', 'Gestopt sinds...'] as const;

export enum LeverziektenOpties {
    ernstig = 'Ernstig',
    geen = 'Geen',
    mild = 'Mild',
}

export enum ComorbiditeitenJaNeeOpties {
    ja = 'Ja',
    nee = 'Nee',
}

export enum ComorbiditeitenMedicatieOpties {
    aanwezig = 'Aanwezig',
    aanwezig_met_medicatie = 'Aanwezig met medicatie',
    geen = 'Geen',
}
export enum ComorbiditeitenBehandelingenOpties {
    aanwezig = 'Aanwezig',
    aanwezig_met_behandeling = 'Aanwezig met behandeling',
    geen = 'Geen',
}
export enum DiabetesMellitusOpties {
    type_1_eindorgaanschade = 'Type 1, eindorgaanschade',
    type_1_ongecompliceerd = 'Type 1, ongecompliceerd',
    type_2_eindorgaanschade = 'Type 2, eindorgaanschade',
    type_2_ongecompliceerd = 'Type 2, ongecompliceerd',
}
export enum MaligniteitenOpties {
    geen = 'Geen',
    lokaal = 'Lokaal',
    metastasen = 'Metastase(n)',
}

export enum TabakOpties {
    anders = 'Anders',
    heeft_nooit_gerookt = 'Heeft nooit gerookt',
    niet_roker_maar_rookgedrag_in_verleden_onbekend = 'Niet-roker, maar rookgedrag in verleden onbekend',
    rookt_dagelijks_tabak = 'Rookt dagelijks tabak',
    rookt_passief_tabak = 'Rookt passief tabak',
    rookt_soms_tabak = 'Rookt soms tabak',
    rookte_vroeger = 'Rookte vroeger',
}

export enum AstmaCopdOpties {
    aanwezig = 'Aanwezig',
    aanwezig_met_inhalatiecorticosteroiden = 'Aanwezig met inhalatiecorticosteroïden',
    geen = 'Geen',
}

export enum FollowUpStatusOpties {
    beter = 'Beter',
    de_novo = 'De novo',
    gelijk = 'Gelijk',
    genezen = 'Genezen',
    niet_van_toepassing = 'Niet van toepassing',
    slechter = 'Slechter',
}

export enum AdjuvanteGewichtsreducerendeMedicatieOpties {
    anders = 'Anders',
    liraglutide = 'Liraglutide',
    naltrexon_bupropion = 'Naltrexon/bupropion',
    orlistat = 'Orlistat',
    semaglutide = 'Semaglutide',
    setmelanotide = 'Setmelanotide',
}

export enum TherapeutischeMedicamenteuzeVeranderingOpties {
    geen_verandering = 'Geen verandering',
    gestopt = 'Gestopt',
    verhoging = 'Verhoging',
    verlaging = 'Verlaging',
}

export const dumpingklachten = ['Vroege dumping', 'Late dumping', 'Geen klachten'] as const;
export const bariPostNurseSpecialistFollowUpExperts = [
    Expert.lifestylecoach,
    Expert.psycholoog,
    Expert.dietist,
] as const;

export type OntlastingType = (typeof ontlastingType)[number];
export type RokenStatus = (typeof rokenStatus)[number];
export type BariPostNurseSpecialistFollowUpExperts = (typeof bariPostNurseSpecialistFollowUpExperts)[number];

export interface IAdditionalExamination {
    key: keyof AnderOnderzoek;
    label: string;
    textAreaFieldKey: keyof AnderOnderzoekToelichting;
}

export const additionalExaminations: IAdditionalExamination[] = [
    {
        key: 'cardiologisch_onderzoek',
        label: 'Cardiologisch onderzoek',
        textAreaFieldKey: 'toelichting_cardiologisch_onderzoek',
    },
    {
        key: 'onderzoek_interne_geneeskunde',
        label: 'Onderzoek interne geneeskunde',
        textAreaFieldKey: 'toelichting_onderzoek_interne_geneeskunde',
    },
    {
        key: 'slaaponderzoek',
        label: 'Slaaponderzoek',
        textAreaFieldKey: 'toelichting_slaaponderzoek',
    },
    {
        key: 'longfunctieonderzoek',
        label: 'Longfunctieonderzoek',
        textAreaFieldKey: 'toelichting_longfunctieonderzoek',
    },
    {
        key: 'ander_onderzoek',
        label: 'Ander onderzoek',
        textAreaFieldKey: 'toelichting_ander_onderzoek',
    },
];

export type AdditionalConclusionInCustomControlData = {
    key: keyof ConclusieAanvullend;
    label: string;
    text: string;
};

export const additionalConclusionsInCustomControlData: AdditionalConclusionInCustomControlData[] = [
    {
        key: 'conclusie_aanvullend_diabetes_mellitus',
        label: 'Diabetes mellitus',
        text: 'Het verzoek is om de co-morbiditeit te blijven controleren en zo nodig medicatie aan te passen, waarbij het advies is dit de eerste zes maanden maandelijks te verrichten. Er dient maximaal twaalf maanden pre-operatief en maximaal zes maanden postoperatief een funduscontrole plaats te vinden, in verband met een verhoogde kans op toename van retinopathie na bariatrische chirurgie. De kans op deze proliferatieve diabetische retinopathie is met name verhoogd bij snelle verbetering van de glycemische regulatie.',
    },
    {
        key: 'conclusie_aanvullend_hydrochloorthiazide',
        label: 'Gebruik hydrochloorthiazide',
        text: 'De dag van de operatie zal de hydrochloorthiazide worden gestaakt, gezien het risico op dehydratie en/of hypokaliemie in de postoperatieve periode. Wij verzoeken de huisarts om een week na de operatie de bloeddruk van patiënt te controleren gezien het staken van de hydrochloorthiazide vanaf de dag van de operatie. Na deze controle is het verzoek om de bloeddruk maandelijks te controleren gedurende de eerste 6 maanden na de bariatrische ingreep.',
    },
    {
        key: 'conclusie_aanvullend_psychofarmaca',
        label: 'Gebruik psychofarmaca',
        text: 'Wij verzoeken de huisarts aandacht te hebben voor het feit dat psychofarmaca mogelijk verminderd worden opgenomen na bariatrische chirurgie. Patiënt wordt geadviseerd met de behandelaar te bespreken of controle van spiegels en/of ophogen van de dosering, eventueel op geleide van beeld, nodig is. Gezien het obesogene effect van antidepressiva is het verzoek om de noodzaak van het gebruik nogmaals te heroverwegen, zo nodig in samenspraak met de psychiater.',
    },
    {
        key: 'conclusie_aanvullend_roken',
        label: 'Roken',
        text: 'Patiënt wordt geadviseerd om te stoppen met roken. Roken verhoogt het operatierisico en verhoogt postoperatief de kans op marginale ulcera en naadlekkage.',
    },
];

export type AdditionalConclusion = Override<
    AdditionalConclusionInCustomControlData,
    { key: keyof ConclusieAanvullend | 'conclusie_aanvullend_NSAID' | 'conclusie_aanvullend_anticonceptiva' }
>;

export const additionalConclusions: AdditionalConclusion[] = [
    ...additionalConclusionsInCustomControlData,
    {
        key: 'conclusie_aanvullend_NSAID',
        label: 'Gebruik NSAID',
        text: "Verzoek aan de huisarts om aandacht te hebben voor het gebruik van NSAID's, deze kunnen na bariatrische chirurgie eerder aanleiding geven tot klachten (irritatie maagslijmvlies, ontstaan van ulceraties). Geadviseerd wordt deze medicatie niet voor te schrijven, alleen op strikte indicatie en dan altijd samen met een protonpompremmer. De voorkeur gaat uit naar Tramadol of, in het geval van jicht, naar Colchicine.",
    },
    {
        key: 'conclusie_aanvullend_anticonceptiva',
        label: 'Gebruik anticonceptiva',
        text: "Verzoek aan de huisarts om aandacht te besteden aan de mogelijk verminderde werkzaamheid van orale anticonceptiva na bariatrische chirurgie, alsmede voor de risico’s van zwangerschap het eerste jaar na bariatrische chirurgie. Geadviseerd wordt om te starten met een andere vorm van anticonceptie bijvoorbeeld een IUD danwel Implanon. De 'prikpil' wordt ontraden vanwege de verhoogde kans op osteoporose.",
    },
];

export type ReferralIndication = {
    key: keyof Omit<
        IndicatieVerwijzing,
        'indicatie_voor_verwijzing_overig' | 'indicatie_voor_verwijzing_overig_toelichting'
    >;
    label: string;
    text: string;
};

export const referralIndications: ReferralIndication[] = [
    {
        key: 'indicatie_voor_verwijzing_diabetes',
        label: 'Diabetes',
        text: 'Patienten met diabetes en een HbA1c >75mmol/mol -> overleggen met Interne – in principe behandeling via eigen behandelaar (internist/ huisarts)\nType 1 ja -> verwijzing Interne\nType 2 ja -> insuline gebruik en/of  SGLT2 remmer gebruik? (i: Canagliflozine, Dapagliflozine, Empagliflozide, Ertugliflozine) -> ja -> verwijzing Interne',
    },
    {
        key: 'indicatie_voor_verwijzing_hypertensie',
        label: 'Hypertensie',
        text: 'Met 1 of 2 antihypertensivum geen verwijzing nodig\nMet 3 of meer antihyperstensiva -> overleg interne of verwijzing nodig is',
    },
    {
        key: 'indicatie_voor_verwijzing_nefrologie',
        label: 'Nefrologie',
        text: 'Chronische nierinsufficiëntie (eGFR < 45 danwel bekend bij nefroloog -> overleg interne of verwijzing nodig is.\nNierstenen in VG zonder eerdere analyse -> overleg interne of verwijzing nodig is.',
    },
    {
        key: 'indicatie_voor_verwijzing_medicatie',
        label: 'Medicatie',
        text: '1. Gebruik DOAC (rivarozaban/xarelto, apixaban/eliquis, edoxaban/lixiana, dabigatran/pradaxa) -> verwijzing interne\n2. Clopidogrel/persantin ivm (cardiale)stent < 6 maanden -> uitgestelde OK; > 6 maanden overleg eigen cardioloog/neuroloog of het tijdelijk gestaakt mag worden\n3. Reuma met embrel/methotrexaat/biological -> overleg eigen reumatoloog, mag medicatie 2 maanden gestaakt worden?',
    },
    {
        key: 'indicatie_voor_verwijzing_osteoporose',
        label: 'Osteoporose',
        text: 'Behandeling voor osteoporose (o.a. alendroninezuur en zoledroninezuur) -> verwijzing interne',
    },
    {
        key: 'indicatie_voor_verwijzing_hartfalen',
        label: 'Hartfalen',
        text: 'Preoperatief en kort postoperatief consult eigen cardioloog inplannen of er akkoord voor OK is',
    },
    {
        key: 'indicatie_voor_verwijzing_obesitas_specifiek',
        label: 'Obesitas-specifiek',
        text: '1. Tekenen van Cushing (vetafzetting romp, dunne armen en benen, vollemaansgezicht, striae) of sterke toename gewicht in korte periode -> verwijzing interne\n2. Verdenking monogenetische/syndromale obesitas: ontstaan voor leeftijd < 20, met name vanaf peuterleeftijd, geen verzadigingsgevoel, de enige in de familie, voorkomen van leerproblemen/cognitieve beperkingen, aangeboren afwijkingen (hart/nieren), slechtziendheid, slechthorendheid, of macrocephalie) -> verwijzing interne \n3. Patiënten > 67 jaar met uitgebreide co-morbiditeit voor inschatten gezondheidswinst -> overleg met internist of verwijzing nodig is \n4. Patiënten met andere complexe voorgeschiedenis -> overleg met internist of verwijzing nodig is',
    },
];

export type CaroNotificationData<Type extends CaroNotificationType> = {
    dueDate: Date | string;
    type: Type;
};

export type ObesiQQuestionnaireTitle =
    | 'Lichaamsbeeld'
    | 'Eetgedrag'
    | 'Lichamelijke activiteiten'
    | 'Psychisch welbevinden'
    | 'Seksueel welzijn'
    | 'Sociaal welzijn';

export const ObesiQQuestionnaireTitles: ObesiQQuestionnaireTitle[] = [
    'Lichaamsbeeld',
    'Eetgedrag',
    'Lichamelijke activiteiten',
    'Psychisch welbevinden',
    'Seksueel welzijn',
    'Sociaal welzijn',
];
export const obesiQTranslations: Record<
    ObesiQQuestionnaireName,
    { description: string; title: ObesiQQuestionnaireTitle }
> = {
    [QuestionnaireName.obesiQBodyImage]: {
        description:
            'Als u aan uw lichaam denkt, in de afgelopen week, in hoeverre bent u het oneens of eens met iedere stelling:',
        title: 'Lichaamsbeeld',
    },
    [QuestionnaireName.obesiQEatingBehavior]: {
        description: 'Denk aan het eten dat u in de afgelopen week at. Hoe vaak:',
        title: 'Eetgedrag',
    },
    [QuestionnaireName.obesiQPhysicalFunction]: {
        description: 'Als u aan uw lichaam denkt, in de afgelopen week, hoe vaak heeft u problemen gehad met:',
        title: 'Lichamelijke activiteiten',
    },
    [QuestionnaireName.obesiQPsychologicalFunction]: {
        description:
            'Als u aan uw lichaam denkt, in de afgelopen week, in hoeverre bent u het oneens of eens met iedere stelling:',
        title: 'Psychisch welbevinden',
    },
    [QuestionnaireName.obesiQSexualFunction]: {
        description: 'Als u aan uw lichaam denkt, in hoeverre bent u het oneens of eens met iedere stelling:',
        title: 'Seksueel welzijn',
    },
    [QuestionnaireName.obesiQSocialFunction]: {
        description:
            'Als u aan uw lichaam denkt, in de afgelopen week, in hoeverre bent u het oneens of eens met iedere stelling:',
        title: 'Sociaal welzijn',
    },
};

export interface IObesiQQuestionnaireResponse {
    obesiQQuestionnaireName: QuestionnaireName;
    obesiQState: ObesiQState;
    raschTransformedScore: string;
}
export type IObesiQQuestionnaireResponseWithTitle = {
    obesiQQuestionnaireTitle: ObesiQQuestionnaireTitle;
    obesiQState: ObesiQState;
    raschTransformedScore: string;
};

export const mdoInterimReportAdviceWithColor: {
    badgeLabel?: string;
    color: ProcessActivityBadge['color'];
    value: MdoInterimReportAdvice;
}[] = [
    {
        color: 'green',
        value: MdoInterimReportAdvice.include,
    },
    {
        badgeLabel: 'Patiënt kan verder, maar...',
        color: 'yellow',
        value: MdoInterimReportAdvice.continueWithGuidance,
    },
    {
        badgeLabel: 'Patiënt pauzeren',
        color: 'orange',
        value: MdoInterimReportAdvice.notYet,
    },
    {
        color: 'red',
        value: MdoInterimReportAdvice.exclude,
    },
];

export const mdoReportAdviceWithColor: {
    badgeLabel?: string;
    color: ProcessActivityBadge['color'];
    value: MdoReportAdvice;
}[] = [
    {
        color: 'green',
        value: MdoReportAdvice.include,
    },
    {
        badgeLabel: 'Patiënt includeren, maar...',
        color: 'yellow',
        value: MdoReportAdvice.continueWithGuidance,
    },
    {
        badgeLabel: 'Patiënt pauzeren',
        color: 'orange',
        value: MdoReportAdvice.notYet,
    },
    {
        color: 'red',
        value: MdoReportAdvice.exclude,
    },
];
